import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, X } from "lucide-react";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) =>
  new Intl.DateTimeFormat("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(iso));

const inputCls =
  "w-full rounded-xl border border-purple-500/30 bg-[#0D0719] px-4 py-3 text-sm text-white placeholder-slate-500 outline-none transition-colors focus:border-yellow-400/70 focus:ring-2 focus:ring-yellow-400/20";

export default function RegisterModal({ tournament, onClose, onSuccess }) {
  const [form, setForm] = useState({
    teamName: "",
    captainName: "",
    email: "",
    phone: "",
    players: "3",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/register`, {
        tournament_id: tournament.id,
        team_name: form.teamName.trim(),
        captain_name: form.captainName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        number_of_players: Number(form.players),
      });
      if (data.ok) {
        onSuccess({ teamName: form.teamName.trim(), players: form.players, tournament });
      } else {
        toast.error(data.error || "Registration failed — try again.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      data-testid="registration-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 80, opacity: 0 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="glass-card max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl p-6 sm:rounded-3xl sm:p-8"
      >
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <p className="font-mono-b3b text-[10px] uppercase tracking-[0.3em] text-yellow-400">
              Lock your spot
            </p>
            <h3 className="mt-1 font-display text-3xl uppercase leading-none tracking-wide text-white">
              {fmtDate(tournament.start)}
            </h3>
          </div>
          <button
            data-testid="close-modal-btn"
            onClick={onClose}
            className="rounded-full border border-purple-500/30 p-2 text-slate-400 transition-colors hover:border-yellow-400/60 hover:text-yellow-400"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form data-testid="registration-form" onSubmit={submit} className="space-y-4">
          <div>
            <label className="mb-1.5 block font-heading text-xs font-semibold uppercase tracking-widest text-slate-400">
              Team name
            </label>
            <input
              data-testid="input-team-name"
              required
              maxLength={60}
              value={form.teamName}
              onChange={set("teamName")}
              placeholder="e.g. Purple Reign"
              className={inputCls}
            />
          </div>
          <div>
            <label className="mb-1.5 block font-heading text-xs font-semibold uppercase tracking-widest text-slate-400">
              Captain's name
            </label>
            <input
              data-testid="input-captain-name"
              required
              maxLength={60}
              value={form.captainName}
              onChange={set("captainName")}
              placeholder="Who runs this squad?"
              className={inputCls}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block font-heading text-xs font-semibold uppercase tracking-widest text-slate-400">
                Email
              </label>
              <input
                data-testid="input-email"
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="captain@team.com"
                className={inputCls}
              />
            </div>
            <div>
              <label className="mb-1.5 block font-heading text-xs font-semibold uppercase tracking-widest text-slate-400">
                Phone number
              </label>
              <input
                data-testid="input-phone"
                required
                type="tel"
                minLength={5}
                maxLength={25}
                value={form.phone}
                onChange={set("phone")}
                placeholder="+31 6 1234 5678"
                className={inputCls}
              />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block font-heading text-xs font-semibold uppercase tracking-widest text-slate-400">
              Number of players
            </label>
            <select
              data-testid="select-players"
              value={form.players}
              onChange={set("players")}
              className={inputCls}
            >
              <option value="3">3 players — starting five? nah, starting three</option>
              <option value="4">4 players — one sub</option>
              <option value="5">5 players — full rotation</option>
            </select>
          </div>

          <button
            data-testid="submit-registration-btn"
            type="submit"
            disabled={submitting}
            className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full bg-yellow-400 px-6 py-4 font-heading text-sm font-bold uppercase tracking-widest text-black shadow-[0_0_28px_rgba(250,204,21,0.3)] transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-60"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Locking your spot…
              </>
            ) : (
              "Confirm registration"
            )}
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
}
